import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
  Switch,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { launchImageLibrary } from 'react-native-image-picker';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { advanceDirectiveService, AdvanceDirectiveData } from '../../../../services/advanceDirectiveInfo';
import { documentStorageService, FileInfo } from '../../../../services/documentStorageService';
import { debugAuthStatus } from '../../../../utils/authDebug';

import { createLogger } from '../../../../utils/logger';
import { promptForDocumentKey } from '../../../../services/auth/vaultUnlockPrompt';
const logger = createLogger('AdvanceDirectiveInput');

type AdvanceDirectiveInputScreenRouteProp = RouteProp<RootStackParamList, 'AdvanceDirectiveInput'>;

export const AdvanceDirectiveInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<AdvanceDirectiveInputScreenRouteProp>();

  const { advanceDirectiveData, isEditing } = route.params || {};

  const [formData, setFormData] = useState({
    physicalLocation: advanceDirectiveData?.physicalLocation || '',
    attorneyContact: advanceDirectiveData?.attorneyContact || '',
    familyInformed: advanceDirectiveData?.familyInformed || false,
    specificInstructions: advanceDirectiveData?.specificInstructions || '',
    hasDigitalCopy: advanceDirectiveData?.hasDigitalCopy || false,
  });

  const [selectedFile, setSelectedFile] = useState<FileInfo | null>(null);
  const [saving, setSaving] = useState(false);

  const handleFilePick = async () => {
    try {
      const result = await launchImageLibrary({
        mediaType: 'mixed',
        includeBase64: false,
        maxHeight: 2000,
        maxWidth: 2000,
        quality: 1,
        selectionLimit: 1,
      });

      if (result.assets && result.assets.length > 0) {
        const asset = result.assets[0];
        const fileInfo: FileInfo = {
          uri: asset.uri || '',
          name: asset.fileName || 'advance-directive.pdf',
          size: asset.fileSize || 0,
          type: asset.type || 'application/pdf',
        };

        setSelectedFile(fileInfo);
        setFormData(prev => ({ ...prev, hasDigitalCopy: true }));
      } else if (result.didCancel) {
        logger.debug('User cancelled file picker');
      }
    } catch (error) {
      logger.error('Error picking file:', error);
      Alert.alert('Error', 'Failed to pick file. Please try again.');
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFormData(prev => ({ ...prev, hasDigitalCopy: false }));
  };

  const handleSave = async () => {
    // Debug authentication status
    logger.debug('[Advance Directive] Checking auth status before save...');
    await debugAuthStatus();

    // Validation
    if (!formData.hasDigitalCopy && !formData.physicalLocation.trim()) {
      Alert.alert('Missing Information', 'Please either upload a document or specify where the physical document is stored.');
      return;
    }

    try {
      setSaving(true);

      let documentId: string | undefined;

      // If user uploaded a digital copy, store it in document repository
      if (formData.hasDigitalCopy && selectedFile) {
        // Check storage limit before upload
        const storageCheck = await documentStorageService.canAddFile(selectedFile.size);

        if (!storageCheck.canAdd) {
          const tierName = storageCheck.storageType === 'local' ? 'Plus' : 'Premium';
          Alert.alert(
            '🚫 Storage Limit Reached',
            `You've reached your ${tierName} storage limit.\n\n` +
            `Current usage: ${storageCheck.usedMB}MB / ${storageCheck.limitMB}MB\n` +
            `File size: ${storageCheck.fileSizeMB}MB\n\n` +
            (storageCheck.upgradeMessage || 'Please delete some documents to continue.'),
            [{ text: 'OK' }]
          );
          setSaving(false);
          return;
        }

        // Generate document ID
        documentId = `doc_advance_directive_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        // Upload to document storage
        logger.debug('[Advance Directive] Uploading document with ID:', documentId);
        const uploadResult = await documentStorageService.storeDocument(
          selectedFile,
          {
            documentId,
            category: 'legal',
            documentType: 'PDF',
            fileName: formData.specificInstructions || 'Advance Directive',
            fileSize: selectedFile.size,
            tags: ['legal', 'advance-directive']
          }
        );

        if (!uploadResult.success) {
          throw new Error(uploadResult.error || 'Failed to upload document');
        }

        logger.debug('[Advance Directive] Document uploaded successfully');
      }

      // Save metadata to advance directive service
      const advanceDirectiveUpdateData: Omit<AdvanceDirectiveData, 'id'> = {
        documentId,
        hasDigitalCopy: formData.hasDigitalCopy,
        physicalLocation: formData.physicalLocation.trim() || undefined,
        attorneyContact: formData.attorneyContact.trim() || undefined,
        familyInformed: formData.familyInformed,
        lastUpdated: new Date().toISOString(),
        specificInstructions: formData.specificInstructions.trim() || undefined,
      };

      let savedAdvanceDirective;

      let resultData;
      if (isEditing && advanceDirectiveData?.id) {
        // Update existing record
        savedAdvanceDirective = await advanceDirectiveService.updateAdvanceDirective(
          advanceDirectiveData.id,
          advanceDirectiveUpdateData
        );
        // Handle wrapped response format { success, data, message }
        resultData = savedAdvanceDirective?.data || savedAdvanceDirective;
        logger.debug('Advance directive updated successfully with ID:', resultData.id);
      } else {
        // Create new record
        savedAdvanceDirective = await advanceDirectiveService.createAdvanceDirective(advanceDirectiveUpdateData);
        // Handle wrapped response format { success, data, message }
        resultData = savedAdvanceDirective?.data || savedAdvanceDirective;
        logger.debug('Advance directive saved successfully with ID:', resultData.id);
      }

      // Navigate to lock/unlock screen
      navigation.navigate('AdvanceDirectiveLockUnlock', { advanceDirectiveId: resultData.id });
    } catch (error) {
      if (promptForDocumentKey(error, { root: 'Dashboard', routes: [{ name: 'Dashboard' }] })) {
        return;
      }
      logger.error('Error saving advance directive:', error);
      Alert.alert('Error', `Failed to save advance directive: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>
          Add Your Advance Directive
        </Text>
        <Text style={styles.subtitle}>
          Upload your document or record where it's stored
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView}>
          {/* Document Upload Section */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Document (Optional)</Text>
            <Text style={styles.sectionDescription}>
              Upload a PDF or image of your advance directive
            </Text>

            {!selectedFile ? (
              <TouchableOpacity style={styles.uploadButton} onPress={handleFilePick}>
                <Image
                  source={require('../../../../assets/icons/upload.png')}
                  style={styles.uploadIcon}
                />
                <Text style={styles.uploadButtonText}>Upload Document</Text>
              </TouchableOpacity>
            ) : (
              <View style={styles.filePreview}>
                <View style={styles.fileInfo}>
                  <Text style={styles.fileName}>{selectedFile.name}</Text>
                  <Text style={styles.fileSize}>
                    {(selectedFile.size / 1024).toFixed(2)} KB
                  </Text>
                </View>
                <TouchableOpacity onPress={handleRemoveFile}>
                  <Text style={styles.removeButton}>Remove</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>

          {/* Physical Location */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Physical Location *</Text>
            <Text style={styles.helperText}>
              Where is the original document stored?
            </Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.physicalLocation}
              onChangeText={(value) => setFormData({ ...formData, physicalLocation: value })}
              placeholder="e.g., Safe deposit box at First National Bank, With attorney, Home safe, Filed at hospital"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={3}
              textAlignVertical="top"
            />
          </View>

          {/* Legal Advisor */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Legal Advisor</Text>
            <Text style={styles.helperText}>
              Attorney or legal advisor contact information
            </Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.attorneyContact}
              onChangeText={(value) => setFormData({ ...formData, attorneyContact: value })}
              placeholder="e.g., John Smith, Smith & Associates, (555) 123-4567, john@smithlaw.com"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={2}
              textAlignVertical="top"
            />
          </View>

          {/* Family Informed Toggle */}
          <View style={styles.toggleRow}>
            <Text style={styles.toggleLabel}>
              Have you informed your family about this document?
            </Text>
            <Switch
              value={formData.familyInformed}
              onValueChange={(value) => setFormData({ ...formData, familyInformed: value })}
              trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
              thumbColor={formData.familyInformed ? '#FFFFFF' : '#FFFFFF'}
            />
          </View>

          {/* Special Instructions */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Special Instructions</Text>
            <Text style={styles.helperText}>
              Any special instructions for accessing or locating the document
            </Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.specificInstructions}
              onChangeText={(value) => setFormData({ ...formData, specificInstructions: value })}
              placeholder="e.g., Safe combination is 1234, Ask Dr. Johnson for hospital copy, Key wishes: no heroic measures"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.submitButtonContainer}>
            <TouchableOpacity
              style={[styles.saveButton, saving && { opacity: 0.7 }]}
              onPress={handleSave}
              disabled={saving}
            >
              {saving ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.saveButtonText}>{isEditing ? 'Update' : 'Submit'}</Text>
              )}
            </TouchableOpacity>
          </View>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  subtitle: {
    fontSize: ms(14, 0.3),
    color: '#FFFFFF',
    paddingHorizontal: ms(30),
    textAlign: 'center',
    opacity: 0.9,
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
    paddingBottom: ms(120),
  },
  section: {
    marginBottom: ms(24),
  },
  sectionTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  sectionDescription: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    marginBottom: ms(12),
  },
  uploadButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(12),
    borderWidth: ms(2),
    borderColor: '#29BFC0',
    borderStyle: 'dashed',
    padding: ms(20),
  },
  uploadIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#29BFC0',
    marginRight: ms(10),
  },
  uploadButtonText: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    fontWeight: '600',
  },
  filePreview: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(12),
    padding: ms(16),
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  fileInfo: {
    flex: 1,
  },
  fileName: {
    fontSize: ms(16, 0.3),
    fontWeight: '500',
    color: '#1C2C4F',
    marginBottom: ms(4),
  },
  fileSize: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
  },
  removeButton: {
    fontSize: ms(14, 0.3),
    color: '#FF3B30',
    fontWeight: '600',
  },
  inputGroup: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(4),
  },
  helperText: {
    fontSize: ms(13, 0.3),
    color: '#9CA3AF',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(12),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  textArea: {
    minHeight: s(80),
    textAlignVertical: 'top',
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: ms(20),
    paddingVertical: ms(10),
  },
  toggleLabel: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    flex: 1,
    marginRight: ms(15),
  },
  submitButtonContainer: {
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
  },
  saveButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(60),
    alignItems: 'center',
    marginBottom: ms(120),
    minWidth: s(200),
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  bottomPadding: {
    height: s(120),
  },
});
